import { ENTITY_ID, PHISHING_DIFFICULTY, PHISHING_PAYLOAD_TYPE, PHISHING_USER_STATUS } from '../../domain/domainCodes.js'
import { ApiError, api } from '../../lib/api'
import { dataParamsQuery, readDataTable } from '../../lib/dataTable'
import { download } from '../../lib/download'
import { isEnglish } from '../../lib/i18n'
import { upload } from '../../lib/upload'

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''


/* ------------------------------------------------------------------ *
 * Phishing templates - `Features/Admin/Phishing/PhishingTemplates`, `ContentManagers`.
 *
 *   getTemplatesPage -> GET  /api/admin/phishing-templates/
 *   listTemplates    -> POST /filter          FORM-ENCODED DataTables, PascalCase envelope
 *   getTemplate      -> (no endpoint - read out of /filter, see below)
 *   saveTemplate     -> POST /save            FORM-ENCODED, Id -1 creates
 *   deleteTemplate   -> POST /delete          FORM-ENCODED
 *   importTemplates  -> POST /import          MULTIPART part "file", iframe HTML back
 *   exportTemplates  -> GET  /export?id=      a zip
 *   surfaceUrl       -> GET  /view-*?id=      raw HTML, for an iframe
 *
 * THREE THINGS ABOUT THIS SLICE THAT FAIL SILENTLY:
 *
 * 1. NOTHING HERE TAKES JSON. Every POST is form-encoded; `/filter` goes further and falls
 *    back to the QUERY STRING when the content type is not a form, so posting JSON to it is
 *    a 200 OK with an empty grid rather than a 400.
 * 2. `length` MUST BE SENT. `DataTablesRequest.Length` is a non-nullable int, so omitting it
 *    binds 0 and the handler does `.Take(0)` - again 200 OK, zero rows.
 * 3. FAILURES ARE 200s. The filter handler catches everything and answers
 *    `{Draw, RecordsTotal: 0, RecordsFiltered: 0, Data: [], Error: "<ex.Message>"}`, so the
 *    status code never reports a database error. `Error` is what has to be checked.
 *
 * The envelope is PascalCase here (a typed `DataTablesResponse<T>` returned straight through
 * `Results.Json`), unlike the seven slices that hand-build a camelCase one - `readDataTable`
 * accepts both (API_FINDINGS.md §23).
 */
const TEMPLATES = '/api/admin/phishing-templates'
export const prepareTemplateContent = (body, payloadType, isArabic) => api.post(`${TEMPLATES}/prepare-content`, { Body: body, PayloadType: Number(payloadType), IsArabic: isArabic })
export const previewTemplateContent = (body, isArabic, signal) => api.post(`${TEMPLATES}/preview-content`, { Body: body, PayloadType: PHISHING_PAYLOAD_TYPE.QR_CODE, IsArabic: isArabic }, { signal })

/**
 * A checkbox gives a boolean, a `<Select>` gives the STRING "false" - and `Boolean("false")`
 * is true. Every boolean leaving this module goes through here, because the server compares
 * `.Equals("true", OrdinalIgnoreCase)` and would read the difference as a silent flip:
 * an English template saved as Arabic, tracking switched on by closing the form.
 */
const asBool = (value) => value === true || value === 'true' || value === 1 || value === '1'

/** UTF-8 safe base64 - `btoa` alone throws on any character above U+00FF. */
function toBase64(text) {
  if (!text) return ''
  const bytes = new TextEncoder().encode(text)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

/**
 * THE HTML IS BASE64 TWICE ON THE WIRE, and this is the single most confusing thing about
 * the slice.
 *
 * The DATABASE COLUMN already holds base64 — all 49 stored templates do, and `/save`
 * base64-DECODES what it is given before writing, so base64 is what it expects to store.
 * `/filter` then base64-encodes that column on the way out. So a body arrives as
 * `base64(base64(html))` and has to be sent back the same way.
 *
 * Decoding is tolerant at both layers (the server's `DecodeBase64` catches its
 * FormatException and returns the input unchanged), so a row written as raw HTML before the
 * convention existed still opens correctly — the second decode simply falls through.
 */
const decodeHtml = (value) => fromBase64(fromBase64(value))
const encodeHtml = (value) => toBase64(toBase64(value))

/** ...and back. Anything that is not valid base64 is passed through unchanged. */
function fromBase64(value) {
  if (!value) return ''
  try {
    const binary = atob(value)
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  } catch {
    /* The API decodes with the same tolerance: `DecodeBase64` catches FormatException and
       returns the raw string, so a template written before the encoding existed still
       renders. */
    return value
  }
}

/**
 * The editor's options: the active categories, the three media directories the stored
 * screenshots live under, and the tracking host.
 *
 * A JSON sibling of the retired Razor page, reusing the same query.
 */
export async function getTemplatesPage(signal) {
  const dto = (await api.get(`${TEMPLATES}/`, { signal })) ?? {}

  return {
    categories: (dto.Categories ?? []).map((category) => ({
      value: category.Id,
      label: label(category.ArabicName, category.EnglishName),
    })),
    /* Hardcoded server-side, but read from the payload rather than repeated here - the
       screenshots are addressed through them. */
    directories: {
      email: dto.PhishingTemplatesEmails ?? '',
      landing: dto.PhishingTemplatesLandingPages ?? '',
      after: dto.PhishingTemplatesAfterPhishingPages ?? '',
      attachments: dto.PhishingTemplatesAttachmentsDirectory ?? '',
    },
    phishingUrl: dto.PhishingUrl ?? '',
  }
}

function toTemplateRow(dto) {
  return {
    id: dto.Id,
    name: dto.Name ?? '',
    categoryId: dto.PhishingCategoryId,
    /* NUMBERS AND BOOLEANS, never their string spellings. The form's selects hand back
       strings, and three places in this feature compare with `===` against a number
       (the canonical attachment payload type decides whether the attachment chip is drawn); a string there
       silently draws nothing. Normalised on the way in, and again on the way out. */
    isArabic: Boolean(dto.IsArabic),
    subject: dto.Subject ?? '',
    /* Base64 TWICE — see `decodeHtml`. */
    body: decodeHtml(dto.Body),
    landingPage: decodeHtml(dto.LandingPage),
    afterPage: decodeHtml(dto.RedirectPage),
    trackingImage: Boolean(dto.TrackingImage),
    captureSubmittedData: Boolean(dto.CaptureSubmittedData),
    capturePasswords: Boolean(dto.CapturePasswords),
    redirectToUrl: Boolean(dto.RedirectToUrl),
    // Plain, unlike the three above.
    redirectUrl: dto.RedirectUrl ?? '',
    isActive: Boolean(dto.IsActive),
    difficulty: dto.DifficultyLevel ?? PHISHING_DIFFICULTY.EASY,
    payloadType: dto.PayloadType ?? PHISHING_PAYLOAD_TYPE.WEBSITE,
    lastUpdate: dto.LastUpdateText ?? '',
    attachments: (dto.Attachments ?? []).map((attachment) => ({
      id: attachment.Id,
      name: attachment.Name ?? '',
      mime: attachment.Mime ?? '',
      size: attachment.Size ?? 0,
      map: attachment.Map ?? '',
    })),
  }
}

/**
 * The list. Server-paged, so the screen passes its own page and page size.
 *
 * EVERY ROW CARRIES ITS THREE HTML SURFACES, double-base64, and there is no way to ask for
 * fewer fields — a page of 25 is about 5.6 MB. That is why the default page here is 10, and
 * why nothing in this module ever asks for 100.
 *
 * `RecordsTotal` IS NOT THE TABLE SIZE: the handler counts the already-searched query and
 * assigns the same number to both totals, so the unfiltered size is never sent. The screen
 * is told the filtered total and nothing else, which is what a pager needs anyway.
 */
/** The columns `FilterPhishingTemplatesHandler` can order by, by the names the table sends. */
export const TEMPLATE_SORTABLE = ['name', 'isArabic', 'difficulty', 'payloadType', 'isActive', 'lastUpdate']

export async function listTemplates({ page = 1, pageSize = 10, query = '', sort } = {}, signal) {
  // `length` is never omitted: a missing one binds 0 and the handler returns nothing.
  const params = dataParamsQuery({ page, pageSize, search: query ?? '', sort, columns: TEMPLATE_SORTABLE })

  const dto = await api.post(`${TEMPLATES}/filter`, params, { signal })

  // A database error arrives as a 200 with this set, so it is the only place failure shows.
  if (dto?.Error) throw new ApiError(dto.Error)

  const { rows, total } = readDataTable(dto)
  return { rows: rows.map(toTemplateRow), total }
}

/**
 * One template.
 *
 * THERE IS NO GET-ONE ENDPOINT, and `/filter` has no field projection: it returns every
 * template's email, landing page and after page, each base64 TWICE. Asking for 100 rows is
 * **45 MB and ten seconds** — measured, not estimated. So this must never be the ordinary
 * path, and it is not: the list already holds the row and hands it to the editor through
 * router state.
 *
 * This is the fallback for a bookmarked or refreshed `/phishing/templates/:id/edit`. It
 * scans in SMALL pages and stops at the match, because the rows are ordered by last update
 * and the one being edited is usually near the front — a page of ten costs about 2 MB
 * instead of 45.
 */
export async function getTemplate(id, signal) {
  const wanted = Number(id)
  const PAGE = 10

  for (let page = 1; page <= 60; page += 1) {
    const { rows, total } = await listTemplates({ page, pageSize: PAGE }, signal)
    const found = rows.find((row) => row.id === wanted)
    if (found) return found
    if (rows.length === 0 || page * PAGE >= total) return null
  }

  return null
}

/**
 * Create or update — `Id` of -1 CREATES.
 *
 * Not 0, and not omitted: `int.TryParse(form["Id"], out var id) ? id : ENTITY_ID.NEW` means anything
 * unparseable also creates, so an empty string here would silently add a duplicate instead
 * of failing. The handler tests `request.Id == -1` for the insert branch, so 0 would be read
 * as "update the template with id 0" and answer not-found.
 */
export async function saveTemplate(values) {
  const body = new URLSearchParams()
  body.set('Id', String(Number(values.id) > ENTITY_ID.NONE ? Number(values.id) : ENTITY_ID.NEW))
  body.set('Name', values.name ?? '')
  body.set('PhishingCategoryId', String(Number(values.categoryId) || 0))
  /* Booleans are compared `.Equals("true", OrdinalIgnoreCase)` server-side, so "1" and "on"
     would both read as false — hence `String(asBool(x))` rather than either raw value. */
  body.set('IsArabic', String(asBool(values.isArabic)))
  body.set('Subject', values.subject ?? '')
  body.set('Body', encodeHtml(values.body))
  body.set('TrackingImage', String(asBool(values.trackingImage)))
  body.set('LandingPage', encodeHtml(values.landingPage))
  body.set('CaptureSubmittedData', String(asBool(values.captureSubmittedData)))
  body.set('CapturePasswords', String(asBool(values.capturePasswords)))
  body.set('RedirectToUrl', String(asBool(values.redirectToUrl)))
  body.set('RedirectUrl', values.redirectUrl ?? '')
  body.set('RedirectPage', encodeHtml(values.afterPage))
  body.set('IsActive', String(asBool(values.isActive)))
  body.set('DifficultyLevel', String(Number(values.difficulty) || PHISHING_DIFFICULTY.EASY))
  body.set('PayloadType', String(Number(values.payloadType) || PHISHING_PAYLOAD_TYPE.WEBSITE))

  /* ATTACHMENTS ARE ONE FORM FIELD HOLDING A JSON ARRAY, and a parse failure server-side is
     swallowed — the list becomes null and every existing attachment is DELETED. So it is
     sent as one JSON string, and the existing ones are re-sent with `Base64: "Old"`, which
     is the sentinel that keeps the stored file. The legacy page gets this wrong: jQuery
     serialises its array as `Attachments[0][Id]=…`, `form["Attachments"]` comes back empty,
     and every save there quietly wipes them. */
  body.set(
    'Attachments',
    JSON.stringify(
      (values.attachments ?? []).map((attachment) => ({
        Id: Number(attachment.id) || 0,
        Name: attachment.name ?? '',
        Mime: attachment.mime ?? '',
        Size: Number(attachment.size) || 0,
        Base64: attachment.base64 ?? 'Old',
      })),
    ),
  )

  /* The three screenshots. PNG ONLY — the handler tests for the literal
     `data:image/png;base64,` prefix and refuses anything else — and each is sent only when
     a new one was chosen, because an empty value leaves the stored image alone. */
  for (const [field, value] of [
    ['EmailImage', values.emailImage],
    ['LandingPageImage', values.landingPageImage],
    ['AfterPhishingPageImage', values.afterPhishingPageImage],
  ]) {
    if (value) body.set(field, value)
  }

  try {
    const data = await api.post(`${TEMPLATES}/save`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** The field is `templateId`, lower-cased and unlike every other id in the slice. */
export async function deleteTemplate(id) {
  const body = new URLSearchParams({ templateId: String(Number(id)) })
  try {
    const data = await api.post(`${TEMPLATES}/delete`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * `/view-email`, `/view-landing-page` and `/view-after-page` are NOT USED, and should not be.
 *
 * Each returns the stored column verbatim under a `text/html` content type — and the stored
 * column is base64, so a browser pointed at one renders a wall of base64 rather than the
 * email. (The legacy's `window.open('ViewEmail?id=')` has the same problem.) The preview
 * sheet uses the decoded HTML already on the row instead, which costs no extra request.
 *
 * Recorded in API_FINDINGS.md rather than worked around, because there is nothing here the
 * client needs: `/filter` already carries all three surfaces.
 */

/** A zip — of one template with an id, or of all of them without. */
export async function exportTemplates(id) {
  const suffix = id ? `?id=${Number(id)}` : ''
  try {
    const saved = await download(`${TEMPLATES}/export${suffix}`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * Import a template archive.
 *
 * The part is named `file`, and the answer is not JSON: this is an iframe-era endpoint that
 * replies with `<script>parent.finishImport(ok, 'message')</script>`. `upload()` owns that
 * protocol — it now recognises this third spelling of it alongside `finishUpload` and
 * `finishSavingCategory` — so the SPA parses the reply instead of executing it.
 */
export async function importTemplates(file, { signal } = {}) {
  const body = new FormData()
  body.append('file', file)

  try {
    const data = await upload(`${TEMPLATES}/import`, body, { signal })
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* ------------------------------------------------------------------ *
 * Phishing campaigns - `Features/Admin/Phishing/PhishingCampaigns`, `ContentManagers`.
 *
 *   getCampaignsPage  -> GET  /api/admin/phishing-campaigns/   lookups + the summary
 *   listCampaigns     -> POST /filter        FORM-ENCODED DataTables, PascalCase envelope
 *   saveCampaign      -> POST /save          FORM-ENCODED, Id -1 creates
 *   deleteCampaign    -> POST /delete
 *   setCampaignActive -> POST /set-status
 *   getCampaignSummary-> POST /summary       from/to, LOWERCASE keys
 *
 * THE DATE TRAP, which is the one that matters here. Every date in this slice is read with a
 * bare `DateTime.TryParse` and no culture, and the default request culture is ar-SA — whose
 * .NET calendar is UmAlQura. Under it `2026-08-09 14:30:00`, `2026-08-09` and `2026/08/09
 * 14:30` ALL FAIL TO PARSE, and every failure is swallowed into a fallback: `StartDate`
 * becomes `DateTime.Now`, `EndDate` becomes thirty days later, `SendEmailsBy` becomes null.
 * The campaign then saves "successfully" with dates nobody chose. Only the ISO form with a
 * `T` survives, and it must carry no `Z` — a `Z` is read as UTC and shifted three hours.
 *
 * So every date leaves this module through `toApiDate`, and `StartDateTxt` — which the server
 * pre-formats as `yyyy/MM/dd HH:mm` for Arabic — is NEVER sent back.
 */
const CAMPAIGNS = '/api/admin/phishing-campaigns'

/**
 * `yyyy-MM-ddTHH:mm:ss`, local, no zone suffix. See the note above.
 * Accepts what `<input type="datetime-local">` and `<input type="date">` produce.
 */
function toApiDate(value) {
  if (!value) return ''
  const text = String(value).trim()
  // Already the right shape (with or without seconds).
  const iso = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(:\d{2})?$/.exec(text)
  if (iso) return `${iso[1]}T${iso[2]}${iso[3] ?? ':00'}`
  // A bare date from a date picker.
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `${text}T00:00:00`
  // Anything else: go through Date and rebuild by hand rather than `toISOString`,
  // which converts to UTC and would move the campaign by the timezone offset.
  const date = new Date(text)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  )
}

/**
 * Form inputs support full date and time (`YYYY-MM-DDTHH:mm`).
 */
export const toInputDateTime = (value) => {
  if (!value) return ''
  const text = String(value).trim()
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(text)
  if (m) return `${m[1]}T${m[2]}`
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `${text}T00:00`
  return text.slice(0, 16)
}
const toInputDate = toInputDateTime

/**
 * Formats a campaign date/time for display with AM/PM (e.g. "2026-09-16 11:40 AM").
 */
export function formatCampaignDate(value) {
  if (!value) return ''
  const text = String(value).trim()
  if (!text) return ''

  // Match YYYY-MM-DD or YYYY/MM/DD with optional time
  let m = /^(\d{4}[-/]\d{2}[-/]\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(text)
  if (m) {
    const [, rawDate, hoursStr, minutes] = m
    const date = rawDate.replace(/\//g, '-')
    if (!hoursStr) return date

    let hours = parseInt(hoursStr, 10)
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    const formattedHours = String(hours).padStart(2, '0')
    return `${date} ${formattedHours}:${minutes} ${ampm}`
  }

  // Match DD-MM-YYYY or DD/MM/YYYY with optional time
  m = /^(\d{2}[-/]\d{2}[-/]\d{4})(?:[T ](\d{2}):(\d{2}))?/.exec(text)
  if (m) {
    const [, rawDate, hoursStr, minutes] = m
    const date = rawDate.replace(/\//g, '-')
    if (!hoursStr) return date

    let hours = parseInt(hoursStr, 10)
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    const formattedHours = String(hours).padStart(2, '0')
    return `${date} ${formattedHours}:${minutes} ${ampm}`
  }

  return text
}

/** Returns ISO datetime, preserving user-selected time or falling back to originalIso. */
function withTimeFrom(dateVal, originalIso) {
  if (!dateVal) return ''
  const text = String(dateVal).trim()
  const hasTime = /T(\d{2}:\d{2}(:\d{2})?)/.exec(text)
  if (hasTime) return text
  const date = text.slice(0, 10)
  const time = /T(\d{2}:\d{2}(:\d{2})?)/.exec(String(originalIso ?? ''))
  if (!time) return `${date}T00:00:00`
  return `${date}T${time[1].length === 5 ? `${time[1]}:00` : time[1]}`
}

/**
 * The lookups the form chooses from, and the summary the list counts with.
 *
 * A JSON sibling of the retired Razor page, reusing the same query — it is the only place in
 * the API that answers with the templates, domains, departments, programmes and sender
 * profiles together.
 */
export async function getCampaignsPage(signal) {
  const dto = (await api.get(`${CAMPAIGNS}/`, { signal })) ?? {}

  return {
    templates: (dto.Templates ?? []).map((row) => ({
      value: row.Id,
      id: row.Id,
      label: row.Name ?? '',
      name: row.Name ?? '',
      body: decodeHtml(row.Body),
      landingPage: decodeHtml(row.LandingPage),
      afterPage: decodeHtml(row.AfterPage),
      categoryId: row.CategoryId,
      isArabic: row.IsArabic ?? true,
      payloadType: row.PayloadType ?? PHISHING_PAYLOAD_TYPE.WEBSITE,
    })),
    domains: (dto.Domains ?? []).map((row) => ({
      value: row.Url ?? '',
      label: row.Name ?? row.Url ?? '',
    })),
    groups: (dto.Groups ?? []).map((row) => ({
      value: row.GroupId,
      label: label(row.ArabicName, row.EnglishName),
      typeId: row.GroupTypeId,
    })),
    programs: (dto.LearningPrograms ?? []).map((row) => ({ value: row.Id, label: row.Name ?? '' })),
    /* The mail profile a campaign sends through. `From` is the address, which is what
       distinguishes two profiles with similar names. */
    senders: (dto.EmailSmtpSettings ?? []).map((row) => ({
      value: row.Id,
      label: row.From ? `${row.SettingName} — ${row.From}` : (row.SettingName ?? ''),
    })),
    summary: toSummary(dto.Summary),
  }
}

function toSummary(dto) {
  const totals = dto?.Statistics ?? {}
  return {
    totals: {
      sent: totals.EmailSent ?? 0,
      opened: totals.EmailOpened ?? 0,
      clicked: totals.LinkClicked ?? 0,
      submitted: totals.DataSubmitted ?? 0,
      reported: totals.Reported ?? 0,
    },
    /* PER CAMPAIGN, keyed by id — the list's five count columns come from here. The row the
       list is built on carries no counts at all, so the two are merged (the same shape the
       groups screen uses for its member counts). */
    byCampaign: new Map(
      (dto?.Campaigns ?? []).map((row) => [
        row.Id,
        {
          sent: row.Statistics?.EmailSent ?? 0,
          opened: row.Statistics?.EmailOpened ?? 0,
          clicked: row.Statistics?.LinkClicked ?? 0,
          submitted: row.Statistics?.DataSubmitted ?? 0,
          reported: row.Statistics?.Reported ?? 0,
        },
      ]),
    ),
  }
}

/**
 * A campaign has no STATUS COLUMN — not in the DTO and not in the database. The four states
 * the screen shows are derived from the three facts it does carry, in this order.
 */
export function campaignState(row) {
  if (!row.isActive) return 'inactive'
  if (row.finishedAt) return 'finished'
  if (row.startedAt) return 'running'
  return 'scheduled'
}

function toCampaignRow(dto) {
  return {
    id: dto.Id,
    name: dto.Name ?? '',
    /* THE ROW IS ALSO THE FORM'S VALUE OBJECT — the editor opens on `{...EMPTY, ...row}`, so
       every key the form edits has to be present here under the form's own name, or the
       field opens blank with nothing to say why. */
    templateId: dto.PhishingTemplateId ?? '',
    url: dto.Url ?? '',
    programId: dto.LearningProgramId ?? '',
    senderId: dto.EmailSmtpSettingsId ?? '',
    groupIds: dto.GroupsIds ?? [],
    /* The editable values are the ISO ones, trimmed to what a datetime-local input wants.
       `StartDateTxt` and friends are the server's DISPLAY strings — `yyyy/MM/dd HH:mm` under
       Arabic — and feeding one back into `/save` is exactly what silently resets the dates. */
    startDate: toInputDate(dto.StartDate),
    startAtUtc: dto.StartAtUtc ?? '',
    endDate: toInputDate(dto.EndDate),
    sendEmailsBy: toInputDate(dto.SendEmailsBy),
    /* The original instants, kept so an edit does not drop the time of day. */
    startDateIso: dto.StartDate ?? '',
    endDateIso: dto.EndDate ?? '',
    sendEmailsByIso: dto.SendEmailsBy ?? '',
    startDateText: dto.StartDateTxt ?? '',
    endDateText: dto.EndDateTxt ?? '',
    sendEmailsByText: dto.SendEmailsByTxt ?? '',
    isActive: Boolean(dto.IsActive),
    startedAt: dto.ActualStartedAt ?? null,
    finishedAt: dto.ActualFinishedAt ?? null,
  }
}

/**
 * The list. Server-paged, but the rows are small — sixteen scalars and a list of group ids,
 * a few hundred bytes each — so a full page of 100 is fine here, unlike the templates list.
 *
 * The counts are merged in from the summary; see `toSummary`.
 */
/** The columns `FilterPhishingCampaignsHandler` can order by. The submitted/reported
    counts come from a separate call per row, so they are not among them. */
export const CAMPAIGN_SORTABLE = ['name', 'startDate', 'isActive']

export async function listCampaigns({ page = 1, pageSize = 25, query = '', sort } = {}, signal) {
  // `length` is never omitted: a missing one binds 0 and the handler returns nothing.
  const params = dataParamsQuery({ page, pageSize, search: query ?? '', sort, columns: CAMPAIGN_SORTABLE })

  const dto = await api.post(`${CAMPAIGNS}/filter`, params, { signal })

  /* A database error arrives as a 200 with this set and an empty `Data`, so the status code
     never reports it and an unchecked client draws an empty table instead. */
  if (dto?.Error) throw new ApiError(dto.Error)

  const { rows, total } = readDataTable(dto)
  return { rows: rows.map(toCampaignRow), total }
}

/**
 * Create or update — `Id` of -1 CREATES, and so does anything unparseable.
 *
 * TWO FIELDS THAT FAIL SILENTLY IF YOU GET THEM WRONG:
 *  - `GroupsIds[]` carries the LITERAL brackets. Without them the server sees nothing, and
 *    the campaign saves with no recipients at all, reporting success.
 *  - `IsActive` is compared to the string "true". A checkbox posted alongside a hidden field
 *    would send "true,false", which is not equal to "true", and the campaign goes inactive.
 */
export async function saveCampaign(values) {
  const body = new URLSearchParams()
  body.set('Id', String(Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW))
  body.set('Name', values.name ?? '')
  body.set('Url', values.url ?? '')
  body.set('PhishingTemplateId', String(Number(values.templateId) || 0))
  body.set('StartDate', toApiDate(withTimeFrom(values.startDate, values.startDateIso)))
  body.set('EndDate', toApiDate(withTimeFrom(values.endDate, values.endDateIso)))
  body.set('SendEmailsBy', toApiDate(withTimeFrom(values.sendEmailsBy, values.sendEmailsByIso)))
  // Both are read as null unless they parse ABOVE zero, so an empty choice is simply omitted.
  if (Number(values.programId) > 0) body.set('LearningProgramId', String(Number(values.programId)))
  if (Number(values.senderId) > 0) body.set('EmailSmtpSettingsId', String(Number(values.senderId)))
  body.set('IsActive', values.isActive === false ? 'false' : 'true')
  if (values.audienceFingerprint) body.set('AudienceFingerprint', values.audienceFingerprint)

  for (const groupId of values.groupIds ?? []) body.append('GroupsIds[]', String(Number(groupId)))

  try {
    const data = await api.post(`${CAMPAIGNS}/save`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export async function previewCampaignAudience(groupIds, startDate, signal) {
  const body = new URLSearchParams()
  for (const groupId of groupIds ?? []) body.append('GroupsIds[]', String(Number(groupId)))
  body.set('StartDate', startDate || '')
  return api.post(`${CAMPAIGNS}/audience-preview`, body, { signal })
}

async function campaignAction(path, fields) {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(fields)) body.set(key, String(value))

  try {
    const data = await api.post(`${CAMPAIGNS}/${path}`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* THE ID IS `campaignId`, LOWER-CASED, on both — and `Id` on `/save` alone. A wrong
   name binds 0, which is a well-formed "not found" rather than an error, so the button looks
   like it worked and the row does not move. The status flag is `Status`, not `IsActive`. */
export const deleteCampaign = (id) => campaignAction('delete', { campaignId: Number(id) })
export const setCampaignActive = (id, isActive) =>
  campaignAction('set-status', { campaignId: Number(id), Status: Boolean(isActive) })

/** The aggregate, optionally over a date range. The two keys are LOWERCASE, alone in the slice. */
export async function getCampaignSummary({ from = '', to = '' } = {}, signal) {
  const body = new URLSearchParams()
  if (from) body.set('from', toApiDate(from))
  if (to) body.set('to', toApiDate(to))

  const dto = await api.post(`${CAMPAIGNS}/summary`, body, { signal })
  return toSummary(dto)
}

/* ------------------------------------------------------------------ *
 * Phishing settings and domains — `PhishingSettings` + `PhishingDomains`, both
 * `ContentManagers`.
 *
 *   getPhishingSettings   -> GET  /api/admin/phishing-settings/
 *   togglePhishingSetting -> POST /toggle-setting     { settingKey, isEnabled }
 *   savePhishingSetting   -> POST /save-setting       { settingKey, value }
 *   saveOutlookPlugin     -> POST /save-outlook-plugin
 *   listPhishingCategories-> POST /categories/filter
 *   savePhishingCategory  -> POST /categories/save    Id -1 creates
 *   deletePhishingCategory-> POST /categories/delete  { id }  LOWER-CASE
 *   listPhishingDomains   -> POST /api/admin/phishing-domains/filter
 *   savePhishingDomain    -> POST .../save            Id -1 creates
 *   deletePhishingDomain  -> POST .../delete          { id }  LOWER-CASE
 *   exportPhishingDomains -> GET  .../export
 *   importPhishingDomains -> POST .../save-bulk       MULTIPART part "excelFile"
 *
 * THE FIELD NAMES DIFFER PER ACTION and are written out below rather than derived. Casing is
 * NOT the variable — `IFormCollection` lookups are case-insensitive, so `Id` and `id` are the
 * same field. What differs is the NAME: `settingKey` / `isEnabled` / `value` on the three
 * setting actions, `Id` on both saves, `id` on both deletes. (Contrast the templates slice,
 * where delete genuinely reads a DIFFERENT name, `templateId`, and `Id` binds nothing.)
 *
 * BOTH SETTING ROUTES ARE ALLOW-LISTED. `save-setting` accepts only the four keys in
 * `SETTING_KEYS` and `toggle-setting` only the two in `TOGGLE_KEYS`; anything else is a 200
 * with `Result: false` and "InvalidSettingKey", which reads like a validation failure rather
 * than the typo it is. Note that the header key's STORED NAME is `SkipRequestsWithHeadersList`
 * while the C# constant is called `SkipRequestsWithHeaders` — the stored one is what travels.
 */
const PHISHING_SETTINGS = '/api/admin/phishing-settings'
const PHISHING_DOMAINS = '/api/admin/phishing-domains'

export const SETTING_KEYS = {
  phishingInterval: 'ServiceDelayPhishing',
  listenerInterval: 'ServiceDelayEmailListener',
  phishingUrl: 'PhishingUrl',
  skipHeaders: 'SkipRequestsWithHeadersList',
}

export const TOGGLE_KEYS = {
  phishing: 'ActivatePhishing',
  listener: 'ActivateEmailListener',
}

/** Every setting this screen writes, as it stands now. */
export async function getPhishingSettings(signal) {
  const dto = (await api.get(`${PHISHING_SETTINGS}/`, { signal })) ?? {}

  return {
    phishingEnabled: Boolean(dto.ActivatePhishing),
    listenerEnabled: Boolean(dto.ActivateEmailListener),
    phishingInterval: dto.PhishingServiceInterval ?? '',
    listenerInterval: dto.EmailListenerServiceInterval ?? '',
    phishingUrl: dto.PhishingUrl ?? '',
    /* JSON array of { key, value, isExclude } rules, stored as text. The editor
       preserves the exact-match values and include/exclude behavior on save. */
    skipHeaders: dto.SkipRequestsWithHeaders ?? '[]',
    tenantCode: dto.TenantCode ?? '',
    pluginHost: dto.PluginHost ?? '',
    pluginLogoReady: Boolean(dto.PluginLogoReady),
    pluginVstoReady: Boolean(dto.PluginVstoReady),
    outlook: {
      type: dto.OutlookPluginType ?? '',
      reportingMethod: dto.PluginReportingMethod ?? '',
      reportingEmail: dto.PluginReportingEmail ?? '',
      deleteAfterReport: dto.PluginDeleteAfterReport ?? '',
    },
  }
}

async function settingsAction(path, fields) {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(fields)) body.set(key, String(value))

  try {
    const data = await api.post(`${PHISHING_SETTINGS}/${path}`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export const togglePhishingSetting = (settingKey, isEnabled) =>
  settingsAction('toggle-setting', { settingKey, isEnabled: Boolean(isEnabled) })

export const savePhishingSetting = (settingKey, value) =>
  settingsAction('save-setting', { settingKey, value: value ?? '' })

/**
 * The Outlook plugin block, saved together.
 *
 * `pluginReportingMethod` and `pluginDeleteAfterReport` CANNOT BE CLEARED: the handler skips
 * an empty value rather than storing one, so a chosen method can be changed but never
 * un-chosen. Recorded rather than worked around — the screen offers no "none".
 */
export const saveOutlookPlugin = ({ type, reportingMethod, reportingEmail, deleteAfterReport }) =>
  settingsAction('save-outlook-plugin', {
    type: type ?? '',
    pluginReportingMethod: reportingMethod ?? '',
    pluginReportingEmail: reportingEmail ?? '',
    pluginDeleteAfterReport: deleteAfterReport ?? '',
  })

/** Direct download endpoints for Outlook Add-in deployment artifacts */
export const getOutlookPluginManifestUrl = () => `${PHISHING_SETTINGS}/outlook-plugin/manifest`
export const getOutlookPluginBundleUrl = () => `${PHISHING_SETTINGS}/outlook-plugin/bundle`
export const getOutlookPluginVstoPackageUrl = () => `${PHISHING_SETTINGS}/outlook-plugin/vsto-package`

/**
 * DataTables again, PascalCase envelope, `length` required.
 *
 * `sortable` is the list of column names the handler behind `url` declares in its sort
 * map; `sort.key` has to be one of them to travel, and the handler falls back to its own
 * order for anything else.
 */
async function listSmall(url, { page = 1, pageSize = 25, query = '', sort } = {}, sortable = [], signal) {
  const params = dataParamsQuery({ page, pageSize, search: query ?? '', sort, columns: sortable })

  const dto = await api.post(url, params, { signal })
  if (dto?.Error) throw new ApiError(dto.Error)
  return readDataTable(dto)
}

export const CATEGORY_SORTABLE = ['nameAr', 'nameEn', 'isActive']

export async function listPhishingCategories(options, signal) {
  const { rows, total } = await listSmall(`${PHISHING_SETTINGS}/categories/filter`, options, CATEGORY_SORTABLE, signal)
  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      name: label(dto.ArabicName, dto.EnglishName),
      nameAr: dto.ArabicName ?? '',
      nameEn: dto.EnglishName ?? '',
      isActive: Boolean(dto.IsActive),
    })),
    total,
  }
}

export async function savePhishingCategory(values) {
  const body = new URLSearchParams({
    // -1 creates.
    Id: String(Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW),
    ArabicName: values.nameAr ?? '',
    EnglishName: values.nameEn ?? '',
    IsActive: String(Boolean(values.isActive)),
  })

  try {
    const data = await api.post(`${PHISHING_SETTINGS}/categories/save`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** The handler refuses a category any template still uses, and says so — show its message. */
export const deletePhishingCategory = (id) =>
  settingsAction('categories/delete', { id: Number(id) })

export const DOMAIN_SORTABLE = ['name', 'url']

export async function listPhishingDomains(options, signal) {
  const { rows, total } = await listSmall(`${PHISHING_DOMAINS}/filter`, options, DOMAIN_SORTABLE, signal)
  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      name: dto.Name ?? '',
      url: dto.Url ?? '',
    })),
    total,
  }
}

async function domainAction(path, body) {
  try {
    const data = await api.post(`${PHISHING_DOMAINS}/${path}`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export const savePhishingDomain = (values) =>
  domainAction(
    'save',
    new URLSearchParams({
      Id: String(Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW),
      Name: values.name ?? '',
      Url: values.url ?? '',
    }),
  )

/** Named `id`, like the categories delete beside it. */
export const deletePhishingDomain = (id) =>
  domainAction('delete', new URLSearchParams({ id: String(Number(id)) }))

export async function exportPhishingDomains() {
  try {
    const saved = await download(`${PHISHING_DOMAINS}/export`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** A spreadsheet of domains. The part is named `excelFile`, not `file`. */
export async function importPhishingDomains(file, { signal } = {}) {
  const body = new FormData()
  body.append('excelFile', file)

  try {
    const data = await upload(`${PHISHING_DOMAINS}/save-bulk`, body, { signal })
    return { ok: data?.Result !== false, message: data?.Message ?? '', count: data?.ImportedCount ?? null }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* ------------------------------------------------------------------ *
 * The two mailboxes — `EmailSmtpSettings` + `EmailImapSettings`, both `ContentManagers`.
 *
 *   listSmtpProfiles   -> POST /api/admin/email-smtp-settings/filter    DataTables
 *   saveSmtpProfile    -> POST .../save        Id -1 creates
 *   deleteSmtpProfile  -> POST .../delete      { id }  LOWER-CASE, as the siblings
 *   testSmtpProfile    -> POST .../test        the form as typed + Address
 *   exportSmtpProfiles -> GET  .../export
 *   importSmtpProfiles -> POST .../save-bulk   MULTIPART part "excelFile"
 *   listImapProfiles   -> POST /api/admin/email-imap-settings/filter
 *   saveImapProfile    -> POST .../save        Id -1 creates
 *   deleteImapProfile  -> POST .../delete      { id }
 *   testImapProfile    -> POST .../test        opens the folder, as the listener does
 *
 * THE PASSWORD ONLY TRAVELS ONE WAY. `/filter` answers `HasPassword`, never the password
 * itself, and an empty password on save or test means "use the stored one" — so an editor
 * opened on a saved profile leaves that field blank and saving does not wipe it.
 *
 * ONLY ONE MAILBOX IS ACTIVE. Saving an IMAP profile as active deactivates the others,
 * server-side — the list reloads after every save, so the row that lost it updates.
 */
const SMTP_PROFILES = '/api/admin/email-smtp-settings'
const IMAP_PROFILES = '/api/admin/email-imap-settings'

/**
 * MailKit's `SecureSocketOptions`, spelled exactly as the server parses them. Protocol
 * names rather than chrome, so they are not in `strings.*.js`.
 */
export const CONNECTION_METHODS = [
  { value: 'None', label: 'None' },
  { value: 'Auto', label: 'Auto' },
  { value: 'SslOnConnect', label: 'SSL on connect' },
  { value: 'StartTls', label: 'StartTLS' },
  { value: 'StartTlsWhenAvailable', label: 'StartTLS when available' },
]

/**
 * The certificate check is THREE-VALUED — unset, on, off — so it is carried as the string
 * the select holds (`''` | `'true'` | `'false'`) rather than a boolean, which would fold
 * "not decided" into "do not validate".
 */
const asTriState = (value) => (value === null || value === undefined ? '' : String(value))

async function profileAction(url, body) {
  try {
    const data = await api.post(url, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export const SMTP_SORTABLE = ['name', 'from', 'host', 'isActive']

export async function listSmtpProfiles(options, signal) {
  const { rows, total } = await listSmall(`${SMTP_PROFILES}/filter`, options, SMTP_SORTABLE, signal)
  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      settingName: dto.SettingName ?? '',
      name: dto.Name ?? '',
      from: dto.From ?? '',
      visibleSenderAddress: dto.VisibleSenderAddress ?? '',
      host: dto.Host ?? '',
      port: dto.Port ?? '',
      username: dto.Username ?? '',
      hasPassword: Boolean(dto.HasPassword),
      connectionMethod: dto.ConnectionMethod ?? '',
      validateServerCertificate: asTriState(dto.ValidateServerCertificate),
      isActive: Boolean(dto.IsActive),
      /* Why a delete can be refused — the campaigns still sending from this profile. */
      campaignsCount: dto.CampaignsCount ?? 0,
    })),
    total,
  }
}

/** The fields the SMTP save and test both post; the password is omitted when left blank. */
function smtpBody(values) {
  const body = new URLSearchParams({
    Id: String(Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW),
    SettingName: values.settingName ?? '',
    Name: values.name ?? '',
    From: values.from ?? '',
    VisibleSenderAddress: values.visibleSenderAddress ?? '',
    Host: values.host ?? '',
    Port: String(values.port ?? ''),
    Username: values.username ?? '',
    ConnectionMethod: values.connectionMethod ?? '',
    ValidateServerCertificate: values.validateServerCertificate ?? '',
  })
  if (values.password) body.set('Password', values.password)
  return body
}

export function saveSmtpProfile(values) {
  const body = smtpBody(values)
  body.set('IsActive', String(Boolean(values.isActive)))
  return profileAction(`${SMTP_PROFILES}/save`, body)
}

export const deleteSmtpProfile = (id) =>
  profileAction(`${SMTP_PROFILES}/delete`, new URLSearchParams({ id: String(Number(id)) }))

/** Sends one message through the profile as typed — nothing is saved by testing. */
export function testSmtpProfile(values, address) {
  const body = smtpBody(values)
  body.set('Address', address ?? '')
  return profileAction(`${SMTP_PROFILES}/test`, body)
}

export async function exportSmtpProfiles() {
  try {
    const saved = await download(`${SMTP_PROFILES}/export`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** The exported spreadsheet, back again. The part is named `excelFile`, as the domains one. */
export async function importSmtpProfiles(file, { signal } = {}) {
  const body = new FormData()
  body.append('excelFile', file)

  try {
    const data = await upload(`${SMTP_PROFILES}/save-bulk`, body, { signal })
    return {
      ok: data?.Result !== false,
      message: data?.Message ?? '',
      count: data?.ImportedCount ?? null,
    }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export const IMAP_SORTABLE = ['host', 'username', 'folderName', 'lastSync', 'isActive']

export async function listImapProfiles(options, signal) {
  const { rows, total } = await listSmall(`${IMAP_PROFILES}/filter`, options, IMAP_SORTABLE, signal)
  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      host: dto.Host ?? '',
      port: dto.Port ?? '',
      username: dto.Username ?? '',
      hasPassword: Boolean(dto.HasPassword),
      folderName: dto.FolderName ?? '',
      connectionMethod: dto.ConnectionMethod ?? '',
      tls: Boolean(dto.TLS),
      deleteCampaignsEmails: Boolean(dto.DeleteCampaignsEmails),
      isActive: Boolean(dto.IsActive),
      /* Written by the listener service, never by this screen. */
      lastSync: dto.LastSync ?? '',
    })),
    total,
  }
}

function imapBody(values) {
  const body = new URLSearchParams({
    Id: String(Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW),
    Host: values.host ?? '',
    Port: String(values.port ?? ''),
    Username: values.username ?? '',
    FolderName: values.folderName ?? '',
    ConnectionMethod: values.connectionMethod ?? '',
  })
  if (values.password) body.set('Password', values.password)
  return body
}

export function saveImapProfile(values) {
  const body = imapBody(values)
  body.set('TLS', String(Boolean(values.tls)))
  body.set('DeleteCampaignsEmails', String(Boolean(values.deleteCampaignsEmails)))
  body.set('IsActive', String(Boolean(values.isActive)))
  return profileAction(`${IMAP_PROFILES}/save`, body)
}

export const deleteImapProfile = (id) =>
  profileAction(`${IMAP_PROFILES}/delete`, new URLSearchParams({ id: String(Number(id)) }))

/** Connects, signs in and opens the folder. The server's own refusal is the message. */
export const testImapProfile = (values) => profileAction(`${IMAP_PROFILES}/test`, imapBody(values))

/* ------------------------------------------------------------------ *
 * Phishing report — `Features/Admin/Phishing/PhishingReport`, `ContentManagers`.
 *
 *   getPhishingReport    -> GET  /api/admin/phishing-report/                the whole funnel
 *   getCampaignReport(id)-> GET  /api/admin/phishing-report/campaign/{id}   one campaign's
 *   listReportUsers      -> POST /filter    FORM-ENCODED, campaignId in the BODY
 *   exportReportUrl      -> GET  /export?campaignId=&type=Excel&search=
 *
 * THE STATUS NUMBERS ARE THE CLIENT'S PROBLEM. `/filter` ships `Status` as the raw database
 * integer rendered as a string ("-1".."4") and does not localise it, unlike the two page
 * payloads and unlike `/export` — the same field name means three different things across the
 * slice. The canonical scale is `Domain/Enums/PhishingUserStatus`:
 *
 *   -1 Scheduled · 0 Sent · 1 EmailOpened · 2 LinkClicked · 3 DataSubmitted · 4 UserReported
 *
 * Verified against the live database, where `PhishingCampaignUsers.StatusId` holds exactly
 * -1..3 and the event table -1..4 — nothing is 5 or 6. That matters because a FOURTH numbering
 * exists in `Coeia.Rawam.Phishing.Listener` (1..6) and a fifth was transcribed into this app's
 * user-details screen; see API_FINDINGS.md §54.
 */
const PHISHING_REPORT = '/api/admin/phishing-report'

/** `PhishingUserStatus`, the scale the report rows are on. */
export const PHISHING_STATUS = {
  scheduled: PHISHING_USER_STATUS.SCHEDULED,
  sent: PHISHING_USER_STATUS.SENT,
  emailOpened: PHISHING_USER_STATUS.EMAIL_OPENED,
  linkClicked: PHISHING_USER_STATUS.LINK_CLICKED,
  dataSubmitted: PHISHING_USER_STATUS.DATA_SUBMITTED,
  userReported: PHISHING_USER_STATUS.USER_REPORTED,
}

/** The funnel, in the order a recipient passes through it. */
export const FUNNEL_STAGES = ['sent', 'opened', 'clicked', 'submitted', 'reported']

function toStatistics(dto) {
  return {
    sent: dto?.EmailSent ?? 0,
    opened: dto?.EmailOpened ?? 0,
    clicked: dto?.LinkClicked ?? 0,
    submitted: dto?.DataSubmitted ?? 0,
    reported: dto?.Reported ?? 0,
  }
}

/**
 * The whole-programme report: one funnel, and one row per campaign.
 *
 * WHAT THE FIGURES ACTUALLY COUNT, because the names mislead:
 *  - `sent` is the number of RECIPIENT ROWS, including those still Scheduled. Nothing was
 *    necessarily sent.
 *  - `opened` is cumulative — everyone at status 1 or beyond.
 *  - `clicked` counts statuses 2 and 3 ONLY, so a recipient who clicked and then reported
 *    drops out of it.
 *  - `reported` is driven by a DATE column (`UserReportingDate`), not by status 4.
 */
export async function getPhishingReport(signal) {
  const dto = (await api.get(`${PHISHING_REPORT}/`, { signal })) ?? {}

  return {
    totals: toStatistics(dto.Statistics),
    campaigns: (dto.Campaigns ?? []).map((row) => ({
      id: row.Id,
      name: row.Name ?? '',
      /* Named CreatedDate, but it is `ActualStartedAt` — empty for a campaign that never
         started. The legacy column header calls it the start date, correctly. */
      startedOn: row.CreatedDate ?? '',
      // Already localised server-side, unlike the recipient rows' `Status`.
      status: row.Status ?? '',
      totals: toStatistics(row.Statistics),
    })),
  }
}

/** One campaign's heading facts and its funnel. The recipients come from `listReportUsers`. */
export async function getCampaignReport(campaignId, signal) {
  const dto = (await api.get(`${PHISHING_REPORT}/campaign/${Number(campaignId)}`, { signal })) ?? {}

  return {
    id: dto.Id,
    name: dto.Name ?? '',
    startedOn: dto.CreatedDate ?? '',
    status: dto.Status ?? '',
    payloadType: dto.PayloadType ?? PHISHING_PAYLOAD_TYPE.WEBSITE,
    totals: toStatistics(dto.Statistics),
  }
}

/**
 * The recipients of one campaign, server-paged.
 *
 * `campaignId` TRAVELS IN THE FORM BODY, not the query, and it is not validated: omit it and
 * the handler drops the predicate and answers with recipients from EVERY campaign — a 200
 * that quietly widens a security report. It is always sent.
 *
 * A JSON body here is a 500, not an empty grid: the endpoint calls `ReadFormAsync()`
 * unconditionally, before the DataTables mapper's own query-string fallback can help.
 */
/** The columns `FilterPhishingReportUsersHandler` can order by. */
export const REPORT_USER_SORTABLE = ['name', 'email', 'department', 'status', 'phishedCount', 'reportedOn']

export async function listReportUsers(
  { campaignId, page = 1, pageSize = 25, query = '', from = '', to = '', status = '', sort },
  signal,
) {
  // `length` is never omitted: a missing one binds 0 and the handler takes no rows.
  const params = dataParamsQuery(
    { page, pageSize, search: query ?? '', sort, columns: REPORT_USER_SORTABLE },
    { campaignId: Number(campaignId), from, to, status },
  )

  const dto = await api.post(`${PHISHING_REPORT}/filter`, params, { signal })

  // A database error arrives as a 200 with this set and an empty `Data`.
  if (dto?.Error) throw new ApiError(dto.Error)

  const { rows, total } = readDataTable(dto)
  return { rows: rows.map(toReportUserRow), total }
}

function toReportUserRow(dto) {
  return {
    // A Guid, not an int — the campaign-user row, not the user.
    id: dto.Id,
    userId: dto.UserId,
    username: dto.Username ?? '',
    name: dto.Name ?? '',
    email: dto.Email ?? '',
    department: dto.Department ?? '',
    campaign: dto.Campaign ?? '',
    /* A RAW INTEGER as a string. Parsed here so the screen can compare and colour it; the
       label is looked up from the same scale the export uses. */
    status: Number(dto.Status),
    reportedOn: dto.ReportingDate ?? '',
    phishedCount: dto.PhishedCount ?? 0,
    hasClicked: dto.HasClicked ?? ([PHISHING_USER_STATUS.LINK_CLICKED, PHISHING_USER_STATUS.DATA_SUBMITTED].includes(Number(dto.Status)) || (dto.Events ?? []).some(e => [PHISHING_USER_STATUS.LINK_CLICKED, PHISHING_USER_STATUS.DATA_SUBMITTED].includes(Number(e.EventName)))),
    firstClickDate: dto.FirstClickDate ?? (dto.Events ?? []).filter(e => [PHISHING_USER_STATUS.LINK_CLICKED, PHISHING_USER_STATUS.DATA_SUBMITTED].includes(Number(e.EventName))).at(-1)?.Timestamp ?? '',
    failedCampaignCount: dto.FailedCampaignCount ?? 0,
    sentCampaignCount: dto.SentCampaignCount ?? 0,
    otherFailedCampaignCount: dto.OtherFailedCampaignCount ?? 0,
    events: (dto.Events ?? []).map((event) => ({
      status: Number(event.EventName),
      at: event.Timestamp ?? '',
      metadata: event.MetaData ?? '',
      /* `IPAddress` KEEPS ITS CAPITALS on the wire — the serializer writes C# names verbatim,
         so a client reaching for `ipAddress` gets undefined. */
      ip: event.IPAddress ?? '',
      userAgent: event.UserAgent ?? '',
      forwardedFor: event.ForwardedFor ?? '',
    })),
  }
}

/**
 * Where the spreadsheet comes from.
 *
 * `campaignId` and `type` are BARE, NON-NULLABLE PRIMITIVES, so both bind from the query and
 * both are REQUIRED — omitting either is a 400 raised by the binder before the handler runs.
 * `type` accepts the single value `"Excel"`, compared ordinally: `"excel"` is refused, and the
 * refusal is a 500 rather than a 400 because the handler answers `Results.Problem` with no
 * status. Only `search` is optional.
 */
export async function exportReport(campaignId, search = '', status = '') {
  const params = new URLSearchParams({ campaignId: String(Number(campaignId)), type: 'Excel' })
  if (search) params.set('search', search)
  if (status !== '') params.set('status', status)

  try {
    const saved = await download(`${PHISHING_REPORT}/export?${params}`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}
